import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, Select, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import WithdrawPointsModal from "../FosWallet/WithdrawPointsModal";

const baseURL = "https://sandboxrnl.vjoneparivaar.com/api";
const axiosInstance = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const fmt = (v) =>
  "₹" + new Intl.NumberFormat("en-IN").format(v || 0);

const monthShort = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleString("en-US", { month: "short" }) : "—";

const getFYAndQuarter = (q) => {
  if (!q) return { fy: "—", qNum: "—", qRange: "—" };
  const fy = q.financial_year ? `FY ${q.financial_year}` : "—";
  const qNum = q.quarter || "—";
  const qRange =
    q.start_date && q.end_date
      ? `${monthShort(q.start_date)} – ${monthShort(q.end_date)}`
      : "—";
  return { fy, qNum, qRange };
};

const EarningsSummaryCard = ({
  currentQuarter, prevQuarter, dashboardLoading,
  allQuarters = [], selectedIdx = 0, onQuarterChange, actualCurrentIdx = -1,
}) => {
  const navigate = useNavigate();
  const [openRedeem, setOpenRedeem] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveBalance, setLiveBalance] = useState(0);
  const [redeemableBalance, setRedeemableBalance] = useState(0);

  const loggedInUser = useSelector((state) => state.Auth.user);
  const fosId = loggedInUser?.fosId;
  const fosName = loggedInUser?.name || "FOS";

  // All derived directly from currentQuarter — no extra API needed
  const totalEarnings = currentQuarter?.total_earning || 0;
  const bookingEarned =
    (currentQuarter?.basic_redeemable || 0) +
    (currentQuarter?.incentive_redeemable || 0) +
    (currentQuarter?.incentive_locked || 0);
  const siteVisitEarned =
    (currentQuarter?.site_visit_redeemable || 0) +
    (currentQuarter?.site_visit_locked || 0);
  const pendingAmt =
    (currentQuarter?.incentive_locked || 0) +
    (currentQuarter?.site_visit_locked || 0);
  const quarterLabel = currentQuarter
    ? `${currentQuarter.financial_year} · ${currentQuarter.quarter}`
    : "";

  // Previous quarter — show comparison only if prev quarter has earnings
  const prevEarnings = prevQuarter?.total_earning || 0;
  const prevLabel = prevQuarter?.quarter || "";
  const hasPrevData = prevQuarter != null && prevEarnings > 0;

  // % change badge — only show if API gives a real value
  const qoqPct = currentQuarter?.vs_prev_quarter?.total_earning_pct ?? null;
  const maxBar = Math.max(totalEarnings, prevEarnings) || 1;

  useEffect(() => {
    if (!fosId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [balanceRes, redeemRes] = await Promise.all([
          axiosInstance.get(`/fosWallet/fosWalletBalance?fos_id=${fosId}`),
          axiosInstance.get(`/fosWallet/getFosFinalRedeemableBalance?fos_id=${fosId}`),
        ]);
        setLiveBalance(balanceRes.data?.data?.totalBalance || 0);
        setRedeemableBalance(redeemRes.data?.finalRedeemableBalance || 0);
      } catch (err) {
        console.error("EarningsSummaryCard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fosId]);

  return (
    <Box
      sx={{
        background:
          "radial-gradient(800px 380px at 90% -20%, rgba(255,255,255,.18), transparent 60%), linear-gradient(135deg, #2C36A8 0%, #4F5DE7 45%, #6E7FF0 100%)",
        borderRadius: 3,
        p: 2.5,
        color: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* SVG grid pattern */}
      <Box
        component="svg"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.08, pointerEvents: "none" }}
        aria-hidden="true"
      >
        <defs>
          <pattern id="earn-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M24 0H0V24" fill="none" stroke="white" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#earn-grid)" />
      </Box>

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%" }}>

        {/* Quarter selector + status */}
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          {allQuarters.length > 0 ? (
            <Select
              value={selectedIdx}
              onChange={(e) => onQuarterChange && onQuarterChange(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              variant="standard"
              disableUnderline
              renderValue={(val) => {
                const q = allQuarters[val];
                if (!q) return null;
                const { fy, qNum, qRange } = getFYAndQuarter(q);
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "rgba(255,255,255,.70)" }}>{fy}</Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#fff" }}>{qNum}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,.65)" }}>{qRange}</Typography>
                  </Box>
                );
              }}
              sx={{
                bgcolor: "rgba(255,255,255,.15)",
                px: 1.5,
                py: 0.3,
                borderRadius: "999px",
                color: "#fff",
                fontSize: "11px",
                "& .MuiSelect-icon": { color: "rgba(255,255,255,.70)", fontSize: "16px" },
                "& .MuiSelect-select": { pr: "20px !important", py: 0 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "14px",
                    boxShadow: "0 16px 48px rgba(44,54,168,0.22), 0 4px 16px rgba(0,0,0,0.10)",
                    bgcolor: "#fff",
                    border: "1px solid rgba(79,93,231,0.14)",
                    mt: 0.75,
                    minWidth: 210,
                    overflow: "hidden",
                    "& .MuiList-root": { py: 0.5 },
                  },
                },
              }}
            >
              {allQuarters.map((q, idx) => {
                const { fy, qNum, qRange } = getFYAndQuarter(q);
                const isCurrent = idx === actualCurrentIdx;
                const isSelected = idx === selectedIdx;
                return (
                  <MenuItem
                    key={q.start_date || idx}
                    value={idx}
                    sx={{
                      py: 0.65, px: 1, mx: 0.5, mb: 0.25,
                      borderRadius: "8px",
                      display: "flex", alignItems: "center", gap: 1,
                      bgcolor: isSelected ? "#EEF2FF" : "transparent",
                      "&.Mui-selected": { bgcolor: "#EEF2FF" },
                      "&.Mui-selected:hover": { bgcolor: "#E0E7FF" },
                      "&:hover": { bgcolor: "#F8FAFC" },
                    }}
                  >
                    <Box sx={{
                      width: 30, height: 30, borderRadius: "8px",
                      bgcolor: isSelected ? "#4F46E5" : "#EEF2FF",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 900, color: isSelected ? "#fff" : "#4F46E5", lineHeight: 1 }}>
                        {qNum}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#0F172A", lineHeight: 1.4 }}>{fy}</Typography>
                      <Typography sx={{ fontSize: "10px", color: "#94A3B8", lineHeight: 1.2 }}>{qNum} · {qRange}</Typography>
                    </Box>
                    {isCurrent && (
                      <Box sx={{
                        fontSize: "9px", fontWeight: 700,
                        px: 0.75, py: 0.25,
                        bgcolor: "#DCFCE7", color: "#15803D",
                        border: "1px solid #BBF7D0",
                        borderRadius: "20px", whiteSpace: "nowrap",
                      }}>
                        NOW
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          ) : quarterLabel ? (
            <Box sx={{ bgcolor: "rgba(255,255,255,.15)", px: 1.5, py: 0.3, borderRadius: "999px", fontSize: "11px", fontWeight: 600 }}>
              {quarterLabel}
            </Box>
          ) : null}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5, color: "#6ee7b7", fontSize: "11px", fontWeight: 600 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#6ee7b7" }} />
            On track
          </Box>
        </Box>

        {/* Greeting + total */}
        <Box mb={1.5}>
          <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,.80)" }}>
            Hi {fosName} 👋 — total earnings this quarter
          </Typography>
          <Box display="flex" alignItems="flex-end" gap={1.5} flexWrap="wrap" mt={0.5}>
            {dashboardLoading ? (
              <CircularProgress size={28} sx={{ color: "rgba(255,255,255,.70)", my: 0.5 }} />
            ) : (
              <Typography sx={{ fontSize: "44px", fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                {fmt(totalEarnings)}
              </Typography>
            )}

            {/* % badge — only shown when API returns a real value */}
            {!dashboardLoading && qoqPct != null && (
              <Box
                sx={{
                  mb: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: qoqPct >= 0 ? "#6ee7b7" : "#fca5a5",
                  bgcolor: qoqPct >= 0 ? "rgba(52,211,153,.15)" : "rgba(239,68,68,.15)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                {qoqPct >= 0
                  ? <TrendingUpIcon sx={{ fontSize: 14 }} />
                  : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                {qoqPct > 0 ? "+" : ""}{qoqPct}% vs prev Q
              </Box>
            )}
          </Box>
        </Box>

        {/* Previous quarter comparison — hidden when no meaningful previous data */}
        {hasPrevData && (
          <Box sx={{ bgcolor: "rgba(255,255,255,.10)", borderRadius: 2, p: 1.5, border: "1px solid rgba(255,255,255,.15)", mb: 2 }}>
            <Box display="flex" justifyContent="space-between" sx={{ fontSize: "11px", mb: 1 }}>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,.70)" }}>
                Previous quarter · {prevLabel}
              </Typography>
              <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>{fmt(prevEarnings)}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Typography sx={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: 1, color: "rgba(255,255,255,.55)", width: 36 }}>This Q</Typography>
              <Box sx={{ flex: 1, height: 6, bgcolor: "rgba(255,255,255,.15)", borderRadius: "999px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", borderRadius: "999px", background: "linear-gradient(135deg,#B45309,#F59E0B,#FCD34D)", width: `${(totalEarnings / maxBar) * 100}%` }} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: 1, color: "rgba(255,255,255,.55)", width: 36 }}>Prev Q</Typography>
              <Box sx={{ flex: 1, height: 6, bgcolor: "rgba(255,255,255,.15)", borderRadius: "999px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", borderRadius: "999px", bgcolor: "rgba(255,255,255,.40)", width: `${(prevEarnings / maxBar) * 100}%` }} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Earnings split */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {/* Booking */}
          <Box sx={{ bgcolor: "rgba(255,255,255,.10)", borderRadius: 2, p: 1.5, border: "1px solid rgba(251,191,36,.30)" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "linear-gradient(135deg,#B45309,#F59E0B,#FCD34D)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}>🏆</Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: 1, color: "#fde68a", fontWeight: 700 }}>Booking</Typography>
                  <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,.70)" }}>Booking incentive earned</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 800 }}>{fmt(bookingEarned)}</Typography>
            </Box>
            <Box sx={{ mt: 1, height: 6, bgcolor: "rgba(255,255,255,.15)", borderRadius: "999px", overflow: "hidden" }}>
              <Box sx={{ height: "100%", borderRadius: "999px", background: "linear-gradient(135deg,#B45309,#F59E0B,#FCD34D)", width: `${totalEarnings ? (bookingEarned / totalEarnings) * 100 : 0}%` }} />
            </Box>
          </Box>

          {/* Site Visit */}
          <Box sx={{ bgcolor: "rgba(255,255,255,.10)", borderRadius: 2, p: 1.5, border: "1px solid rgba(52,211,153,.20)" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "linear-gradient(135deg,#047857,#10B981,#34D399)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}>📍</Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: 1, color: "#6ee7b7", fontWeight: 700 }}>Site visit</Typography>
                  <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,.70)" }}>Site visit incentive earned</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 800 }}>{fmt(siteVisitEarned)}</Typography>
            </Box>
            <Box sx={{ mt: 1, height: 6, bgcolor: "rgba(255,255,255,.15)", borderRadius: "999px", overflow: "hidden" }}>
              <Box sx={{ height: "100%", borderRadius: "999px", background: "linear-gradient(135deg,#047857,#10B981,#34D399)", width: `${totalEarnings ? (siteVisitEarned / totalEarnings) * 100 : 0}%` }} />
            </Box>
          </Box>
        </Box>

        {/* Live balance */}
        <Box sx={{ bgcolor: "rgba(255,255,255,.15)", borderRadius: 2, px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid rgba(255,255,255,.25)", mb: 2 }}>
          <Box>
            <Box display="flex" alignItems="center" gap={0.75} sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1, color: "rgba(255,255,255,.70)" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6ee7b7", flexShrink: 0 }} />
              Live balance · redeemable now
            </Box>
            {loading ? (
              <CircularProgress size={20} sx={{ color: "rgba(255,255,255,.70)", mt: 0.5 }} />
            ) : (
              <Typography sx={{ fontSize: "24px", fontWeight: 800, mt: 0.25 }}>{fmt(liveBalance)}</Typography>
            )}
          </Box>
          <Box textAlign="right">
            <Typography sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1, color: "rgba(255,255,255,.70)" }}>Pending</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{fmt(pendingAmt)}</Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Box display="flex" gap={1} mt="auto">
          <Button
            variant="contained"
            onClick={() => setOpenRedeem(true)}
            disabled={loading}
            sx={{ bgcolor: "#fff", color: "#3038A8", fontWeight: 700, fontSize: "13px", px: 2, "&:hover": { bgcolor: "#e0e7ff" }, textTransform: "none" }}
          >
            Redeem {loading ? "..." : fmt(redeemableBalance)}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/Home/FosWallet")}
            sx={{ borderColor: "rgba(255,255,255,.20)", color: "#fff", fontWeight: 600, fontSize: "13px", px: 2, "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.10)" }, textTransform: "none" }}
          >
            View payouts
          </Button>
        </Box>

      </Box>

      <WithdrawPointsModal
        open={openRedeem}
        onClose={() => setOpenRedeem(false)}
        fosFinalReedemableBalance={redeemableBalance}
        getfosFinalReedemableBalance={async () => {
          try {
            const res = await axiosInstance.get(`/fosWallet/getFosFinalRedeemableBalance?fos_id=${fosId}`);
            setRedeemableBalance(res.data?.finalRedeemableBalance || 0);
            const balRes = await axiosInstance.get(`/fosWallet/fosWalletBalance?fos_id=${fosId}`);
            setLiveBalance(balRes.data?.data?.totalBalance || 0);
          } catch (e) {
            console.error(e);
          }
        }}
      />
    </Box>
  );
};

export default EarningsSummaryCard;
